import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useMercenary } from '../../../contexts/MercenaryContext';
import styles from './DailyMissions.module.css';
import MissionCard from './MissionCard';

interface Mission {
  id: number;
  missionId: number;
  name: string;
  description: string;
  missionType: string;
  requirement: number;
  progress: number;
  silverReward: number;
  xpReward: number;
  isClaimed: boolean;
  isCompleted: boolean;
}

interface ClaimNotice {
  missionId: number;
  silver: number;
  xp: number;
  bonusAwarded: boolean;
}

export default function DailyMissions() {
  const { mercenary, mercenaryId } = useMercenary();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState<Set<number>>(new Set());
  const [rerolling, setRerolling] = useState<number | null>(null);
  const [claimNotice, setClaimNotice] = useState<ClaimNotice | null>(null);

  useEffect(() => {
    fetchMissions();
    const interval = setInterval(fetchMissions, 30000);
    return () => clearInterval(interval);
  }, [mercenaryId]);

  const fetchMissions = async () => {
    if (!mercenaryId) return;
    try {
      setError(null);
      const response = await axios.get(`/api/daily-missions?mercenaryId=${mercenaryId}`, { withCredentials: true });
      setMissions(response.data.missions);
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch missions');
      setLoading(false);
    }
  };

  const handleClaimMission = async (missionId: number) => {
    setClaiming(prev => new Set(prev).add(missionId));
    setError(null);
    try {
      const response = await axios.post(
        `/api/daily-missions/${missionId}/claim`,
        {},
        { withCredentials: true }
      );
      const rewards = response.data.rewards;
      // Optimistically mark as claimed in local state
      setMissions(prev =>
        prev.map(m => m.id === missionId ? { ...m, isClaimed: true } : m)
      );
      setClaimNotice({
        missionId,
        silver: rewards.totalSilver,
        xp: rewards.totalXp,
        bonusAwarded: rewards.bonusAwarded,
      });
      setTimeout(() => setClaimNotice(null), 4000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to claim reward');
    } finally {
      setClaiming(prev => {
        const next = new Set(prev);
        next.delete(missionId);
        return next;
      });
    }
  };

  const handleReroll = async (missionId: number) => {
    setRerolling(missionId);
    try {
      const response = await axios.post(
        `/api/daily-missions/${missionId}/reroll`,
        {},
        { withCredentials: true }
      );
      setMissions(prev =>
        prev.map(m => m.id === missionId ? response.data.mission : m)
      );
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reroll mission');
    } finally {
      setRerolling(null);
    }
  };

  if (loading || !missions) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading daily missions...</div>
      </div>
    );
  }

  const completedCount = missions.filter(m => m.isCompleted).length;
  const claimedCount = missions.filter(m => m.isClaimed).length;
  const unclaimedCompletedCount = missions.filter(m => m.isCompleted && !m.isClaimed).length;
  const allClaimed = claimedCount === 5 && missions.length === 5;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>Daily Missions</h1>
        <p className={styles.subtitle}>Complete each mission to claim its reward individually</p>
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {claimNotice && (
        <div className={styles.success}>
          {claimNotice.bonusAwarded
            ? `Reward claimed! +${claimNotice.silver} silver, +${claimNotice.xp} XP, All 5 done! Completion bonus included.`
            : `Reward claimed! +${claimNotice.silver} silver, +${claimNotice.xp} XP`}
        </div>
      )}

      <div className={styles.missionGrid}>
        {missions.length > 0 ? (
          missions.map(mission => (
            <MissionCard
              key={mission.id}
              mission={mission}
              onReroll={() => handleReroll(mission.id)}
              isRerolling={rerolling === mission.id}
              onClaim={() => handleClaimMission(mission.id)}
              isClaiming={claiming.has(mission.id)}
            />
          ))
        ) : (
          <p className={styles.noMissions}>No missions assigned yet. Check back soon!</p>
        )}
      </div>

      {missions.length > 0 && (
        <div className={styles.summarySection}>
          <div className={styles.progressBlock}>
            <h3>Progress</h3>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${(completedCount / 5) * 100}%` }}
              />
            </div>
            <p className={styles.progressText}>
              {completedCount} / 5 completed, {claimedCount} / 5 claimed
            </p>
          </div>

          <div className={styles.bonusBlock}>
            <h3>Completion Bonus</h3>
            <p className={styles.bonusDesc}>
              Claim all 5 missions to receive an extra <strong>250 silver + 500 XP</strong>.
              {unclaimedCompletedCount > 0 && !allClaimed && (
                <span className={styles.bonusHint}> {unclaimedCompletedCount} ready to claim.</span>
              )}
              {allClaimed && (
                <span className={styles.bonusDone}> All done for today!</span>
              )}
            </p>
          </div>
        </div>
      )}

      <div className={styles.infoSection}>
        <h3>How It Works</h3>
        <ul>
          <li>Five missions are randomly assigned each day at midnight UTC</li>
          <li>Complete each mission by participating in game activities</li>
          <li>Claim each mission reward individually once it is completed</li>
          <li>Each day grants one free reroll to swap a mission you do not want</li>
          <li>A bonus of 250 silver and 500 XP is awarded with the last claim of the day</li>
        </ul>
      </div>
    </div>
  );
}
